import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useAppContext } from '../AppContext';

function CreateBountyModal({ onClose, onBountyCreated }) {
    const { appFunctions, setMessage, currentUserProfile } = useAppContext();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [reward, setReward] = useState(100);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (reward > (currentUserProfile?.tokens || 0)) {
            setMessage('You do not have enough Echoes to fund this bounty.', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            const createBountyFn = httpsCallable(appFunctions, 'createBounty');
            const result = await createBountyFn({ title, description, reward: Number(reward) });
            setMessage('Bounty created successfully!', 'success');
            if (onBountyCreated) onBountyCreated(result.data.bountyId);
            onClose();
        } catch (error) {
            console.error("Failed to create bounty:", error);
            setMessage(error.message || 'Failed to create bounty.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-gradient-to-b from-blue-900/90 to-purple-900/90 border border-blue-500/30 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
                    <LucideIcons.X size={24} />
                </button>

                <div className="p-6 sm:p-8">
                    <div className="flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-2xl mb-6 mx-auto">
                        <LucideIcons.Target size={32} className="text-blue-400" />
                    </div>
                    
                    <h2 className="text-2xl font-bold text-center text-white mb-2 font-sans">Post a Community Bounty</h2>
                    <p className="text-center text-blue-200/80 mb-8 text-sm">Offer Echoes to the community for completing a specific challenge or answering a call.</p>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-sm font-bold text-blue-200 mb-1">Bounty Title</label>
                            <input 
                                type="text" 
                                required
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full bg-black/30 border border-blue-500/30 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-400 transition-colors"
                                placeholder="e.g., Best original poem about the stars"
                                maxLength={60}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-blue-200 mb-1">Description & Requirements</label>
                            <textarea 
                                required
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full bg-black/30 border border-blue-500/30 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-400 transition-colors min-h-[100px]"
                                placeholder="Describe exactly what you are looking for..."
                                maxLength={300}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-blue-200 mb-1">Reward Amount (Echoes)</label>
                            <div className="relative">
                                <LucideIcons.Gem size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-yellow-500" />
                                <input 
                                    type="number" 
                                    required
                                    min="10"
                                    max="10000"
                                    value={reward}
                                    onChange={(e) => setReward(e.target.value)}
                                    className="w-full bg-black/30 border border-blue-500/30 rounded-xl pl-12 pr-4 py-3 text-white font-bold focus:outline-none focus:border-yellow-500 transition-colors"
                                />
                            </div>
                            <p className="text-xs text-gray-400 mt-2 text-right">Available Balance: <span className="text-yellow-400 font-bold">{currentUserProfile?.tokens || 0}</span></p>
                        </div>

                        <button 
                            type="submit" 
                            disabled={isSubmitting}
                            className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg flex justify-center items-center space-x-2 transition-all ${isSubmitting ? 'bg-gray-600 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white'}`}
                        >
                            {isSubmitting ? (
                                <LucideIcons.Loader className="animate-spin" size={24} />
                            ) : (
                                <>
                                    <span>Fund Bounty</span>
                                    <LucideIcons.CheckCircle size={20} />
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default CreateBountyModal;
